/* eslint-disable react/no-unstable-nested-components */

import { AppCard, Badge, Button, Table } from "@jhool-io/fe-components";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { IProviderSuperviseeCaseloadDashboard } from "../../../../../providers/types/providers.types";
import {
    displayNameInRightFormat,
    formatDate,
} from "../../../../../../utils/helpers";
import useNewBackground from "../../../../../../hooks/useNewBackground";
import ListState from "../../../../../../components/ListState/ListState";
import ChevronRightIcon from "../../../../../../components/Icons/ChevronRight";
import {
    TaskCategory,
    TaskStatus,
} from "../../../../../tasks/types/tasks.types";

interface SuperviseeTasksProps {
    appointments: IProviderSuperviseeCaseloadDashboard[];
    superviseeName: string | null;
}

export default function SuperviseeTasks({
    appointments,
    superviseeName,
}: SuperviseeTasksProps) {
    const useNewBg = useNewBackground();

    const navigate = useNavigate();

    const handleRowStyle = () => {
        return {
            backgroundColor: "#FEF8E9",
            borderBottom: "1px solid #ECEEF2",
        };
    };

    const columns: ColumnDef<IProviderSuperviseeCaseloadDashboard>[] =
        useMemo(() => {
            return [
                {
                    accessorKey: "client",
                    header: "CLIENT",
                    cell: ({ row }) => (
                        <span className="text-black">
                            {displayNameInRightFormat({
                                firstName: row.original.first_name,
                                lastName: row.original.last_name,
                            })}
                        </span>
                    ),
                },
                {
                    accessorKey: "preferred_name",
                    header: "Intake",
                    cell: ({ row }) => {
                        const intakeNoteTask =
                            row.original.tasks && row.original.tasks.length > 0
                                ? row.original.tasks?.find(
                                      (task) =>
                                          task.todo_category ===
                                          TaskCategory.WRITE_INTAKE_NOTE
                                  )
                                : null;

                        if (!intakeNoteTask)
                            return <span className="text-black">N/A</span>;
                        if (intakeNoteTask.status === TaskStatus.COMPLETED) {
                            return (
                                <Badge
                                    bgColor="#BAECD1"
                                    content="Done"
                                    textColor="#0E5730"
                                />
                            );
                        }
                        return (
                            <Button
                                variant="normal"
                                size="auto"
                                onClick={() =>
                                    navigate(
                                        `/add-note?session_history_id=${intakeNoteTask?.metadata?.session_history_id}&show_billable=true&note_type=individual_intake_note`
                                    )
                                }
                            >
                                <Badge
                                    bgColor="#FEC1C6"
                                    textColor="#711920"
                                    content={
                                        <span className="flex items-center gap-x-4">
                                            Due
                                            <ChevronRightIcon
                                                className="size-12"
                                                fill="#711920"
                                            />
                                        </span>
                                    }
                                />
                            </Button>
                        );
                    },
                },
                {
                    accessorKey: "email",
                    header: "SUPERVISION NOTE",
                    cell: ({ row }) => {
                        const supervisionNoteTaskList =
                            row.original.tasks?.filter(
                                (task) =>
                                    task.todo_category ===
                                    TaskCategory.WRITE_SUPERVISION_NOTE
                            ) || [];

                        const notStartedSupervisionNoteTaskList =
                            supervisionNoteTaskList
                                .filter(
                                    (task) =>
                                        task.status === TaskStatus.NOT_STARTED
                                )
                                .sort(
                                    (a, b) =>
                                        new Date(a.due_date).getTime() -
                                        new Date(b.due_date).getTime()
                                );

                        if (notStartedSupervisionNoteTaskList.length > 0) {
                            const taskToDisplay =
                                notStartedSupervisionNoteTaskList[0];
                            return (
                                <Button
                                    variant="normal"
                                    size="auto"
                                    onClick={() =>
                                        navigate(
                                            `/add-note?session_history_id=${taskToDisplay?.metadata?.session_history_id}&show_billable=false&note_type=supervision_note`
                                        )
                                    }
                                >
                                    <Badge
                                        bgColor="#FEC1C6"
                                        textColor="#711920"
                                        content={
                                            <span className="flex items-center gap-x-4">
                                                Due date
                                                {` ${formatDate(
                                                    taskToDisplay.due_date
                                                )}`}
                                                <ChevronRightIcon
                                                    className="size-12"
                                                    fill="#711920"
                                                />
                                            </span>
                                        }
                                    />
                                </Button>
                            );
                        }

                        if (
                            supervisionNoteTaskList.length > 0 &&
                            supervisionNoteTaskList.find(
                                (task) => task.status === TaskStatus.COMPLETED
                            )
                        ) {
                            return (
                                <Badge
                                    bgColor="#BAECD1"
                                    content="Done"
                                    textColor="#0E5730"
                                />
                            );
                        }
                        return <span className="text-black">N/A</span>;
                    },
                },
                {
                    accessorKey: "pronoun",
                    header: "QUARTERLY CHECK",
                    cell: ({ row }) => {
                        const supervisorCheckInNoteTaskList =
                            row.original.tasks?.filter(
                                (task) =>
                                    task.todo_category ===
                                    TaskCategory.WRITE_SUPERVISOR_CHECK_IN_NOTE
                            ) || [];

                        const notStartedSupervisorCheckInNoteTaskList =
                            supervisorCheckInNoteTaskList
                                .filter(
                                    (task) =>
                                        task.status === TaskStatus.NOT_STARTED
                                )
                                .sort(
                                    (a, b) =>
                                        new Date(a.due_date).getTime() -
                                        new Date(b.due_date).getTime()
                                );

                        if (
                            notStartedSupervisorCheckInNoteTaskList.length > 0
                        ) {
                            const taskToDisplay =
                                notStartedSupervisorCheckInNoteTaskList[0];
                            return (
                                <Button
                                    variant="normal"
                                    size="auto"
                                    onClick={() =>
                                        navigate(
                                            `/add-note?session_history_id=${taskToDisplay?.metadata?.session_history_id}&show_billable=false&note_type=supervisor_check_in`
                                        )
                                    }
                                >
                                    <Badge
                                        bgColor="#FEC1C6"
                                        textColor="#711920"
                                        content={
                                            <span className="flex items-center gap-x-4">
                                                Due date
                                                {` ${formatDate(
                                                    taskToDisplay.due_date
                                                )}`}
                                                <ChevronRightIcon
                                                    className="size-12"
                                                    fill="#711920"
                                                />
                                            </span>
                                        }
                                    />
                                </Button>
                            );
                        }

                        if (
                            supervisorCheckInNoteTaskList.length > 0 &&
                            supervisorCheckInNoteTaskList.find(
                                (task) => task.status === TaskStatus.COMPLETED
                            )
                        ) {
                            return (
                                <Badge
                                    bgColor="#BAECD1"
                                    content="Done"
                                    textColor="#0E5730"
                                />
                            );
                        }
                        return <span className="text-black">N/A</span>;
                    },
                },
            ];
        }, [navigate]);

    return (
        <>
            {superviseeName ? (
                <h3
                    className={`mb-[22px] text-[20px] font-medium ${
                        useNewBg ? "text-white" : "text-black"
                    }`}
                >{`${superviseeName} Clients`}</h3>
            ) : null}
            <AppCard
                oldCardClass="border border-strokedark bg-white"
                className="px-16 py-[14px] rounded-r12 mt-[22px] mb-24 max-w-full overflow-x-auto"
                mode={useNewBg ? "new" : "old"}
            >
                {appointments.length === 0 ? (
                    <ListState
                        stateHelperText="No supervisee's client list to display"
                        isError={false}
                        emptyMessage="No supervisee's client"
                        cardClass="h-[200px]"
                        mode={useNewBg ? "new" : "old"}
                        context="general"
                    />
                ) : (
                    <Table
                        columns={columns}
                        data={appointments}
                        handleRowStyle={handleRowStyle}
                    />
                )}
            </AppCard>
        </>
    );
}
